import { create } from 'zustand'

import {
  getUserAchievementsApi,
  unlockAchievementApi,
} from '@api'

import { createSelectors } from './helpers'

export interface AchievementBackend {
  id: string
  def_id: string
  account_id: number
  app_id: number
  name: string
  description: string
  icon_url: string
  tags: string
  hidden: boolean
  display_type: string
  created_at: string
  updated_at: string
}

export interface UserAchievement {
  id: string
  defId: string
  accountId: number
  appId: number
  name: string
  description: string
  iconUrl: string
  tags: string
  hidden: boolean
  displayType: string
  createdAt: string
  updatedAt: string
}

export type UnlockedAchievement = UserAchievement

export enum AchievementSlug {
  FIRST_TOUCH = 'first_touch',
  FIRST_GLANCE = 'first_glance',
  ONE_OF_THE_VOICES = 'one_of_the_voices',
  NIGHT_SONG = 'night_song',
  RHYTHM_BEYOND_THE_LOOP = 'rhythm_beyond_the_loop',
  A_SIGN_RECOGNIZED = 'a_sign_recognized',
  WHOS_WATCHING = 'whos_watching',
  EYE_WAS_GIVEN = 'eye_was_given',
  HARLANS_GIFT = 'harlans_gift',
  MASTER_OF_TRIAL_AND_ERROR = 'master_of_trial_and_error',
  FACE_SELECTED = 'face_selected',
  LUCKY = 'lucky',
  TRANSACTION_COMPLETE = 'transaction_complete',
  SUFFERING_IS_OPTIONAL = 'suffering_is_optional',
}

export const ACHIEVEMENT_SLUG_TO_DEF_ID: Record<AchievementSlug, string> = {
  [AchievementSlug.SUFFERING_IS_OPTIONAL]:
    '0198a8c1-194b-4fae-b8ea-9ec20599829f',
  [AchievementSlug.TRANSACTION_COMPLETE]:
    '0198a8c1-194b-4a7e-a09c-50257b2573b8',
  [AchievementSlug.LUCKY]: '0198a8c1-194b-41fd-82e6-d6adb86397cd',
  [AchievementSlug.FACE_SELECTED]: '0198a8c1-194a-423e-b877-b06e708351a8',
  [AchievementSlug.MASTER_OF_TRIAL_AND_ERROR]:
    '0198a8c1-194a-4194-a157-a3019c66f202',
  [AchievementSlug.HARLANS_GIFT]: '0198a8c1-194a-4283-bd26-42a75b051ee8',
  [AchievementSlug.EYE_WAS_GIVEN]: '0198a8c1-194a-48a3-8880-c03364528149',
  [AchievementSlug.WHOS_WATCHING]: '0198a8c1-1949-490d-be23-1228128e3c4d',
  [AchievementSlug.A_SIGN_RECOGNIZED]: '0198a8c1-1949-40f0-b43d-97706924b7c4',
  [AchievementSlug.RHYTHM_BEYOND_THE_LOOP]:
    '0198a8c1-1949-4de0-87db-097fb59f30ed',
  [AchievementSlug.NIGHT_SONG]: '0198a8c1-1949-4072-92cd-c12d189ed112',
  [AchievementSlug.ONE_OF_THE_VOICES]: '0198a8c1-1949-4582-9c11-9bd497ad9b8a',
  [AchievementSlug.FIRST_GLANCE]: '0198a8c1-1949-4b02-8cdc-42fe99c411c7',
  [AchievementSlug.FIRST_TOUCH]: '', // Add placeholder for missing achievement
}

const mapAchievementBackendToUserAchievement = (
  backend: AchievementBackend
): UserAchievement => ({
  id: backend.id,
  defId: backend.def_id,
  accountId: backend.account_id,
  appId: backend.app_id,
  name: backend.name,
  description: backend.description,
  iconUrl: backend.icon_url,
  tags: backend.tags,
  hidden: backend.hidden,
  displayType: backend.display_type,
  createdAt: backend.created_at,
  updatedAt: backend.updated_at,
})

interface AchievementsStore {
  achievements: UserAchievement[]
  isLoading: boolean
  error: string | null
  actions: {
    loadAchievements: () => Promise<void>
    unlockAchievement: (
      slug: AchievementSlug
    ) => Promise<UnlockedAchievement | null>
    getAchievementBySlug: (slug: AchievementSlug) => UserAchievement | undefined
    hasAchievement: (slug: AchievementSlug) => boolean
    clearError: () => void
    reset: () => void
  }
}

const initialState = {
  achievements: [],
  isLoading: false,
  error: null,
}

export const useAchievementsStoreBase = create<AchievementsStore>(
  (set, get) => ({
    ...initialState,
    actions: {
      loadAchievements: async () => {
        set({ isLoading: true, error: null })

        try {
          const response = await getUserAchievementsApi()

          if (response.ok && response.data) {
            const achievements = response.data.map(
              mapAchievementBackendToUserAchievement
            )
            set({ achievements, isLoading: false })
          } else {
            set({
              error: 'Failed to load achievements',
              isLoading: false,
            })
          }
        } catch (error) {
          console.error('Error loading achievements:', error)
          set({
            error: 'Error loading achievements',
            isLoading: false,
          })
        }
      },

      unlockAchievement: async (slug: AchievementSlug) => {
        set({ isLoading: true, error: null })

        const isAchievement = get().actions.hasAchievement(slug)

        if (isAchievement) {
          set({ isLoading: false })
          return null
        }

        try {
          const response = await unlockAchievementApi(slug)

          if (response.ok && response.data) {
            const unlockedAchievement = mapAchievementBackendToUserAchievement(
              response.data
            )

            set((state) => {
              const existingAchievement = state.achievements.find(
                (achievement) => achievement.defId === unlockedAchievement.defId
              )

              if (!existingAchievement) {
                return {
                  achievements: [...state.achievements, unlockedAchievement],
                  isLoading: false,
                }
              }

              return { isLoading: false }
            })

            return unlockedAchievement
          } else {
            set({
              error: 'Failed to unlock achievement',
              isLoading: false,
            })
            return null
          }
        } catch (error) {
          console.error('Error unlocking achievement:', error)
          set({
            error: 'Error unlocking achievement',
            isLoading: false,
          })
          return null
        }
      },

      getAchievementBySlug: (slug: AchievementSlug) => {
        return get().achievements.find(
          (achievement) =>
            achievement.defId === ACHIEVEMENT_SLUG_TO_DEF_ID[slug]
        )
      },

      hasAchievement: (slug: AchievementSlug) => {
        return get().achievements.some(
          (achievement) =>
            achievement.defId === ACHIEVEMENT_SLUG_TO_DEF_ID[slug]
        )
      },

      clearError: () => {
        set({ error: null })
      },

      reset: () => {
        set(initialState)
      },
    },
  })
)

export const useAchievementsStore = createSelectors(useAchievementsStoreBase)

export const useAchievementsActions = () =>
  useAchievementsStoreBase((state) => state.actions)
